import React, { useMemo } from 'react';
import { produce } from 'immer';
import TabItem from './TabItem';
import { useExamSessionStore } from '../../../stores/StoreContext';

/**
 * IncidentReportTab Component
 * 
 * A specialized tab component for the Incident Report tab that manages
 * its own subscription to the incident report state. This prevents the parent
 * SessionDetails component from re-rendering when the incident report changes.
 * 
 * Shows a notification dot when there is a draft incident report that needs attention.
 * 
 * Uses Immer for immutable state updates to ensure optimal performance.
 * 
 * @param {Object} props
 * @param {string} props.tabKey - Unique identifier for the tab (should be 'incidentReport')
 * @param {Object} props.option - Base tab configuration object
 * @param {string} props.option.label - Display label for the tab
 * @param {boolean} props.isSelected - Whether this tab is currently selected
 * @param {Function} props.onClick - Click handler for the tab
 */
function IncidentReportTab({ tabKey, option, isSelected, onClick }) {
    // Subscribe to incident report only in this component
    // This prevents parent re-renders when the incident report changes
    const incidentReport = useExamSessionStore((state) => state.incidentReport);

    // Check if there's a draft incident report
    const hasDraftReport = incidentReport?.draft === true;

    // Create an enhanced option with notification dot if there's a draft using Immer
    const enhancedOption = useMemo(
        () =>
            produce(option, (draft) => {
                if (!draft.notification) {
                    draft.notification = {
                        persistWhenSelected: true,
                        showValue: false
                    };
                }
                // Always set persistWhenSelected for incident report
                draft.notification.persistWhenSelected = true;
                draft.notification.showValue = false;
                if (hasDraftReport) {
                    draft.notification.count = 1;
                } else {
                    draft.notification.count = 0;
                }
            }),
        [option, hasDraftReport]
    );
    return (
        <TabItem
            tabKey={tabKey}
            option={enhancedOption}
            isSelected={isSelected}
            onClick={onClick}
        />
    );
}

export default IncidentReportTab;
